import {
  getPlayersSearchOptions,
  getTeamsSearchOptions,
  type PlayerPublic,
  type SideType,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Label,
} from "@win-reality/react-ui";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
  mapTeamDBToOption,
  type TeamOption,
} from "@/components/EventForm/teamOption";
import { VisibilityGroupSelect } from "@/components/VisibilityGroupSelect";

import { setSport } from "../../store/slices/session";
import type { IPlayer } from "../../store/types";

type SelectPitcherModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (player: IPlayer, teamUuid: string) => void;
  isLoading?: boolean;
  title?: string;
  submitLabel?: string;
};

type PlayerOption = {
  value: string;
  label: string;
};

const sideTypeToHandednessMap: Record<SideType, "R" | "L" | "S"> = {
  right: "R",
  left: "L",
  switch: "S",
};

const mapPublicPlayerToIPlayer = (player: PlayerPublic): IPlayer => {
  const [lastName, firstName] = player.known_names?.[0]?.split(",") ?? [];

  return {
    uuid: player.external_ids?.[0] ?? "",
    firstName: firstName?.trim() ?? "",
    lastName: lastName?.trim() ?? "",
    birthdate: "",
    jerseyNumber: "",
    batHand: player.hit_side
      ? sideTypeToHandednessMap[player.hit_side as SideType]
      : "R",
    throwHand: player.pitch_side
      ? sideTypeToHandednessMap[player.pitch_side as SideType]
      : "R",
  };
};

const mapPlayerToOption = (player: PlayerPublic): PlayerOption => ({
  value: player.external_ids?.[0] ?? "",
  label: player.known_names?.[0] ?? "",
});

export const SelectPitcherModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  title = "Select Pitcher",
  submitLabel = "Confirm",
}: SelectPitcherModalProps) => {
  const dispatch = useDispatch();

  // State
  const [visibilityGroup, setVisibilityGroup] = useState<string | null>(null);
  const [teamSearchValue, setTeamSearchValue] = useState("");
  const [teamDebouncedSearch, setTeamDebouncedSearch] = useState("");
  const [selectedTeamUuid, setSelectedTeamUuid] = useState<string | null>(null);
  const [selectedTeamSport, setSelectedTeamSport] = useState<string | null>(
    null,
  );
  const [playerSearchValue, setPlayerSearchValue] = useState("");
  const [playerDebouncedSearch, setPlayerDebouncedSearch] = useState("");
  const [selectedPlayerUuid, setSelectedPlayerUuid] = useState<string | null>(
    null,
  );
  const [selectedPlayer, setSelectedPlayer] = useState<IPlayer | null>(null);

  // Query: Search teams
  const { data: teamsData, isLoading: isLoadingTeams } = useQuery({
    ...getTeamsSearchOptions({
      query: {
        name: teamDebouncedSearch,
        visibilityGroup: visibilityGroup!,
      },
    }),
    enabled: !!visibilityGroup,
    select: (result) => result?.teams?.map(mapTeamDBToOption) ?? [],
  });

  // Query: Search players within team
  const { data: playersData, isLoading: isLoadingPlayers } = useQuery({
    ...getPlayersSearchOptions({
      query: {
        visibilityGroup: visibilityGroup!,
        teamId: selectedTeamUuid!,
        name: playerDebouncedSearch || undefined,
      },
    }),
    enabled: !!visibilityGroup,
    select: (result) => result?.players ?? [],
  });

  // Reset state when visibility group changes
  useEffect(() => {
    cleanUpState();
  }, [visibilityGroup]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      cleanUpState();
      setVisibilityGroup(null);
    }
  }, [isOpen]);

  const cleanUpState = () => {
    setTeamSearchValue("");
    setTeamDebouncedSearch("");
    setSelectedTeamUuid(null);
    setSelectedTeamSport(null);
    setPlayerSearchValue("");
    setPlayerDebouncedSearch("");
    setSelectedPlayerUuid(null);
    setSelectedPlayer(null);
  };

  const handleTeamSelect = (teamUuid: string) => {
    const team = teamsData?.find((t) => t.value === teamUuid);
    if (team) {
      setSelectedTeamUuid(team.value);
      setSelectedTeamSport(team.meta?.sport ?? null);
      // Reset player selection when team changes
      setPlayerSearchValue("");
      setPlayerDebouncedSearch("");
      setSelectedPlayerUuid(null);
      setSelectedPlayer(null);
    }
  };

  const handlePlayerSelect = (playerUuid: string) => {
    const player = playersData?.find(
      (p) => (p.external_ids?.[0] ?? "") === playerUuid,
    );
    if (player) {
      setSelectedPlayerUuid(playerUuid);
      setSelectedPlayer(mapPublicPlayerToIPlayer(player));
    }
  };

  const handleConfirm = () => {
    if (!selectedPlayer || !selectedTeamUuid) return;

    if (selectedTeamSport) {
      dispatch(setSport(selectedTeamSport));
    }

    onSubmit(selectedPlayer, selectedTeamUuid);
  };

  const handleClose = () => {
    cleanUpState();
    setVisibilityGroup(null);
    onClose();
  };

  const canSubmit = !!selectedPlayer && !!selectedTeamUuid;

  const teamOptions: TeamOption[] = teamsData ?? [];
  const playerOptions: PlayerOption[] =
    playersData?.map(mapPlayerToOption) ?? [];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-4 py-6">
              {/* Visibility Group Selector */}
              <div>
                <Label className="mb-2 block text-sm font-medium">
                  Visibility Group
                </Label>
                <VisibilityGroupSelect
                  value={visibilityGroup}
                  onChange={(value) => {
                    setVisibilityGroup(value);
                    cleanUpState();
                  }}
                />
              </div>

              {/* Team Search */}
              <div>
                <Label className="mb-2 block text-sm font-medium">
                  Team Name
                </Label>
                {visibilityGroup ? (
                  <Autocomplete
                    selectedValue={selectedTeamUuid ?? undefined}
                    onSelectedValueChange={(value) =>
                      handleTeamSelect(value as string)
                    }
                    searchValue={teamSearchValue}
                    onSearchValueChange={setTeamSearchValue}
                    onSearchDebouncedValueChange={setTeamDebouncedSearch}
                    items={teamOptions}
                    isLoading={isLoadingTeams}
                    placeholder="Search teams..."
                    emptyMessage="No teams found"
                    variant="dark"
                    className="w-full"
                    popoverContentClassName="z-[110]"
                    itemRenderer={(option) => (
                      <span className="text-accent-dark-1">{option.label}</span>
                    )}
                  />
                ) : (
                  <div className="flex h-10 items-center rounded-sm border border-border-input bg-bg-input px-3">
                    <span className="text-sm text-placeholder">
                      Select visibility group first
                    </span>
                  </div>
                )}
              </div>

              {/* Player Search */}
              <div>
                <Label className="mb-2 block text-sm font-medium">
                  Pitcher
                </Label>
                {selectedTeamUuid ? (
                  <Autocomplete
                    selectedValue={selectedPlayerUuid ?? undefined}
                    onSelectedValueChange={(value) =>
                      handlePlayerSelect(value as string)
                    }
                    searchValue={playerSearchValue}
                    onSearchValueChange={setPlayerSearchValue}
                    onSearchDebouncedValueChange={setPlayerDebouncedSearch}
                    items={playerOptions}
                    isLoading={isLoadingPlayers}
                    placeholder="Search players..."
                    emptyMessage="No players found"
                    variant="dark"
                    className="w-full"
                    popoverContentClassName="z-[110]"
                    itemRenderer={(option) => (
                      <span className="text-accent-dark-1">{option.label}</span>
                    )}
                  />
                ) : (
                  <div className="flex h-10 items-center rounded-sm border border-border-input bg-bg-input px-3">
                    <span className="text-sm text-placeholder">
                      Select a team first
                    </span>
                  </div>
                )}
              </div>
            </div>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                onClick={handleConfirm}
                isDisabled={!canSubmit}
                isLoading={isLoading}
              >
                {submitLabel}
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
